import { z } from 'zod'

import { CrudService, httpClient, type HttpClient } from '../core'
import { collectionsEndpoints } from './collections.endpoints'
import {
  collectionAssetPageSchema,
  collectionPageSchema,
  collectionSchema,
  collectionUploadSchema,
  type CollectionAssetDto,
  type CollectionCreateBody,
  type CollectionDto,
  type CollectionFilters,
  type CollectionUpdateBody,
  type CollectionUploadDto,
} from './collections.schemas'

/** A safety stop, so a server that ignores `page` can never keep the listing running. */
const MAX_PAGES = 50

/** The page size the files call is documented with. */
const ASSETS_PAGE_SIZE = 50

export class CollectionsService extends CrudService<CollectionDto, CollectionCreateBody, CollectionUpdateBody, CollectionFilters> {
  constructor(http: HttpClient = httpClient) {
    super(http, collectionsEndpoints, { item: collectionSchema, page: collectionPageSchema })
  }

  /** Every template file kept in a collection, page by page. */
  async listAssets(collectionId: string, signal?: AbortSignal): Promise<CollectionAssetDto[]> {
    const items: CollectionAssetDto[] = []
    for (let page = 1; page <= MAX_PAGES; page++) {
      const result = await this.http.get(collectionsEndpoints.assets(collectionId), collectionAssetPageSchema, { query: { page, pageSize: ASSETS_PAGE_SIZE }, signal })
      items.push(...result.items)
      if (result.items.length === 0 || items.length >= result.totalCount) break
    }
    return items
  }

  /** One kept file's bytes, by the file's id: `index` is which of its pictures (its `fileUrl` list), counting from 0. */
  downloadFile(assetId: string, index = 0, signal?: AbortSignal): Promise<Blob> {
    return this.http.download(collectionsEndpoints.download(assetId), { query: { download: false, index }, signal })
  }

  /** Takes one template out of a collection (`DELETE /api/collections/{collectionId}/assets/{assetId}`). The server answers with no body. */
  async removeAsset(collectionId: string, assetId: string, signal?: AbortSignal): Promise<void> {
    await this.http.delete(collectionsEndpoints.asset(collectionId, assetId), z.null(), { signal })
  }

  /** Puts files in a collection, in ONE multipart request (the `files` field repeated). Not retried: a lost request may still have worked. */
  uploadAssets(collectionId: string, files: readonly File[], signal?: AbortSignal): Promise<CollectionUploadDto> {
    const form = new FormData()
    for (const file of files) form.append('files', file, file.name)
    return this.http.post(collectionsEndpoints.assets(collectionId), collectionUploadSchema, { body: form, signal, timeoutMs: 5 * 60_000 })
  }
}

export const collectionsService = new CollectionsService()
